import * as React from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FloatingToolbar,
  Row,
  TouchButton,
  Widget,
  cn,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCar,
  LucideCheck,
  LucideGripVertical,
  LucideLayers,
  LucidePackage,
  LucideSparkles,
  LucideTag,
  LucideTruck,
} from 'lucide-react';

import {
  $addDeduction,
  $addFreightCharge,
  $addLineItem,
  $deleteEntry,
  $itemsInterfaceEntries,
  $itemsInterfaceFacilityDefaultUnit,
  $updateLineItem,
} from '../_state';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { AddCatalyticWidget } from './AddCatalyticWidget';
import { AddDeductionWidget } from './AddDeductionWidget';
import { AddFreightWidget } from './AddFreightWidget';
import { AddMixedWidget } from './AddMixedWidget';
import { AddRawMaterialWidget } from './AddRawMaterialWidget';
import { AddTaggedMaterialWidget } from './AddTaggedMaterialWidget';
import { AddVehicleWidget } from './AddVehicleWidget';

import { $sidebarCollapsed } from '~/app/state/sidebar.state';
import {
  $itemTypeEditMode,
  $saveItemTypeConfig,
  $scaleTicketItemTypeConfig,
} from '~/modules/trade/_state/scaleTicketSettings.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

const ITEM_TYPE_META: Record<
  string,
  {
    icon: React.ReactNode;
    labelKey: keyof typeof import('~/modules/trade/i18n/_en').en.scaleTicketPro.actions;
  }
> = {
  Raw: { icon: <LucidePackage size={18} />, labelKey: 'addMaterial' },
  Tagged: { icon: <LucideTag size={18} />, labelKey: 'addTagged' },
  CatalyticConverter: {
    icon: <LucideSparkles size={18} />,
    labelKey: 'addConverter',
  },
  Mixed: { icon: <LucideLayers size={18} />, labelKey: 'addMixed' },
  Vehicle: { icon: <LucideCar size={18} />, labelKey: 'addVehicle' },
  Freight: { icon: <LucideTruck size={18} />, labelKey: 'addFreight' },
  Deduction: { icon: undefined, labelKey: 'addDeduction' },
};

//
// ⚛️ ⚛️ ⚛️ SORTABLE EDIT BUTTON ⚛️ ⚛️ ⚛️
//
type SortableEditButtonProps = {
  id: string;
  isDisabled: boolean;
  onToggle: () => void;
  label: string;
  icon: React.ReactNode;
};

const SortableEditButton = ({
  id,
  isDisabled,
  onToggle,
  label,
  icon,
}: SortableEditButtonProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="@SortableEditButton flex">
      <TouchButton
        variant="outline"
        className={cn('!rounded-md', isDisabled && 'line-through opacity-40')}
        onPress={onToggle}
      >
        <Row gap1 yCenter>
          <span {...attributes} {...listeners} className="cursor-grab">
            <LucideGripVertical size={14} className="text-foreground/30" />
          </span>
          {icon}
          {label}
        </Row>
      </TouchButton>
    </div>
  );
};

//
// ⚛️ ⚛️ ⚛️ EDIT MODE VIEW ⚛️ ⚛️ ⚛️
//
const EditModeView = () => {
  const t = useTranslations();
  const [config, setConfig] = useAtom($scaleTicketItemTypeConfig);
  const setEditMode = useSetAtom($itemTypeEditMode);
  const saveConfig = useSetAtom($saveItemTypeConfig);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const handleDragEnd = React.useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = config.order.indexOf(String(active.id));
      const newIndex = config.order.indexOf(String(over.id));
      const newOrder = arrayMove(config.order, oldIndex, newIndex);
      setConfig({ ...config, order: newOrder });
    },
    [config, setConfig],
  );

  const handleToggle = React.useCallback(
    (type: string) => {
      const next = { ...config.disabled };
      if (next[type]) {
        delete next[type];
      } else {
        next[type] = true;
      }
      setConfig({ ...config, disabled: next });
    },
    [config, setConfig],
  );

  return (
    <>
      <Widget className="@AddItemsWidget min-w-0 overflow-hidden">
        <Row className="relative min-w-0 p-1.5 text-center" gap="0.375rem">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={config.order}
              strategy={horizontalListSortingStrategy}
            >
              {config.order.map((type) => {
                const meta = ITEM_TYPE_META[type];
                if (!meta) return null;
                return (
                  <SortableEditButton
                    key={type}
                    id={type}
                    isDisabled={!!config.disabled[type]}
                    onToggle={() => handleToggle(type)}
                    label={t.scaleTicketPro.actions[meta.labelKey]}
                    icon={meta.icon}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </Row>
      </Widget>
      <FloatingToolbar visible className="@ItemTypeEditToolbar">
        <Row gap3 yCenter>
          <span className="text-sm">
            {t.scaleTicketPro.settings.itemTypesDescription}
          </span>
          <TouchButton
            variant="outline"
            size="sm"
            onPress={() => setEditMode(false)}
          >
            {t.common.cancel}
          </TouchButton>
          <TouchButton color="primary" size="sm" onPress={() => saveConfig()}>
            <Row gap1 yCenter>
              <LucideCheck size={14} />
              {t.common.save}
            </Row>
          </TouchButton>
        </Row>
      </FloatingToolbar>
    </>
  );
};

//
// ⚛️ ⚛️ ⚛️ NORMAL MODE VIEW ⚛️ ⚛️ ⚛️
//
const NormalModeView = () => {
  const { items: entries = [] } = useAtomValue($itemsInterfaceEntries);
  const facilityDefaultUnit = useAtomValue($itemsInterfaceFacilityDefaultUnit);
  const sidebarCollapsed = useAtomValue($sidebarCollapsed);
  const config = useAtomValue($scaleTicketItemTypeConfig);
  const onAddItem = useSetAtom($addLineItem);
  const onUpdateItem = useSetAtom($updateLineItem);
  const onDeleteItem = useSetAtom($deleteEntry);
  const onAddDeduction = useSetAtom($addDeduction);
  const onAddFreightCharge = useSetAtom($addFreightCharge);
  const modalStyle = useDialogModalStyle();

  const buttonSize = sidebarCollapsed ? 'md' : 'sm';

  const widgetMap: Record<string, React.ReactNode> = {
    Raw: (
      <AddRawMaterialWidget
        key="Raw"
        onAddItem={onAddItem}
        modalStyle={modalStyle}
        buttonSize={buttonSize}
      />
    ),
    Tagged: (
      <AddTaggedMaterialWidget
        key="Tagged"
        onAddItem={onAddItem}
        modalStyle={modalStyle}
        buttonSize={buttonSize}
      />
    ),
    CatalyticConverter: (
      <AddCatalyticWidget
        key="CatalyticConverter"
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        modalStyle={modalStyle}
        buttonSize={buttonSize}
      />
    ),
    Mixed: (
      <AddMixedWidget
        key="Mixed"
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        onDeleteItem={onDeleteItem}
        entries={entries}
        facilityDefaultUnit={facilityDefaultUnit}
        modalStyle={modalStyle}
        buttonSize={buttonSize}
      />
    ),
    Vehicle: (
      <AddVehicleWidget
        key="Vehicle"
        onAddItem={onAddItem}
        onUpdateItem={onUpdateItem}
        buttonSize={buttonSize}
      />
    ),
    Freight: (
      <AddFreightWidget
        key="Freight"
        onAddFreightCharge={onAddFreightCharge}
        buttonSize={buttonSize}
      />
    ),
    Deduction: (
      <AddDeductionWidget
        key="Deduction"
        onAddDeduction={onAddDeduction}
        buttonSize={buttonSize}
      />
    ),
  };

  return (
    <Widget className="@NormalModeView min-w-0 overflow-hidden">
      <Row className="relative min-w-0 p-1.5 text-center" gap="0.375rem">
        {config.order.map((type) =>
          config.disabled[type] ? null : widgetMap[type],
        )}
      </Row>
    </Widget>
  );
};

//
// ⚛️ ⚛️ ⚛️ ADD ITEMS WIDGET ⚛️ ⚛️ ⚛️
//
export const AddItemsWidget = () => {
  const editMode = useAtomValue($itemTypeEditMode);
  return editMode ? <EditModeView /> : <NormalModeView />;
};
